import { AxiosError } from "axios";
import { apiClient } from "lib/apiClient";

/* ---------- P0: doctor-pending conversations (picard SLA logic) ---------- */
// GET /bot/support-portal/conversation/chat-list/
// `is_actionable` is picard's own SLA verdict (bot/support_portal_views.py
// get_actionable_chats_query): qna >15m, doctor_care >25m, followup >60m,
// lactation >150m.

export type ChatType = "qna" | "doctor_care" | "followup" | "lactation";

export interface ChatLastMsg {
  type?: string;
  content?: string;
  text?: string;
}

export interface PendingChat {
  id: number;
  user_id: number;
  user_name: string | null;
  user_mobile: string | null;
  conversation_id: number;
  status: string;
  type: ChatType | string;
  doctor_name: string | null;
  /** who sent the LAST message — "doctor" means a doctor replied last (answered). */
  sender_type: string | null;
  /**
   * Delivery of the doctor's answer, from picard: "delivered" = the outbound WhatsApp
   * message synced (reached the parent), "undelivered" = it never synced (the waplify
   * gap the care team is blind to today), null = no outbound WhatsApp record (unknown).
   */
  delivery: "delivered" | "undelivered" | null;
  last_msg: ChatLastMsg | null;
  updated_at: string | null;
  created_date: string | null;
  is_actionable: boolean;
  is_snoozed: boolean;
}

interface ChatListResponse {
  messages: PendingChat[];
  total_messages: number;
  next_offset: number;
}

export async function fetchPendingChats(): Promise<PendingChat[]> {
  const { data } = await apiClient.get<ChatListResponse>(
    "bot/support-portal/conversation/chat-list/"
  );
  return data.messages ?? [];
}

/* ---------- P0: pending WhatsApp support chats (what triggers the escalation calls) ---------- */
// GET /bot/support-portal/pending-support-chats/ — the EXACT set picard's
// escalate_pending_support_chats cron counts before it pings the doctor group and calls
// the support hotline. So when the team gets a call, they can SEE which parent questions
// are pending instead of being called blind. These are answered by doctors in the app —
// the card LINKS into waplify to read context and can CLOSE a non-question, but it never
// sends anything to the parent.

export interface PendingSupportChat {
  chat_id: number;
  user_name: string | null;
  user_mobile: string | null;
  message: string;
  wait_minutes: number;
  group_name: string | null;
}

export async function fetchPendingSupportChats(): Promise<
  PendingSupportChat[]
> {
  const { data } = await apiClient.get<{ chats: PendingSupportChat[] }>(
    "bot/support-portal/pending-support-chats/"
  );
  return data.chats ?? [];
}

export interface CloseSupportChatResponse {
  chat_id: number;
  status: string;
  /** How many chats remain on the board after the close — picard's own recount. */
  pending_count: number;
}

/**
 * P0 · Close a pending support chat that ISN'T a question — R-for-Rabbit activity
 * chatter ("Bingo", "Trash bag"), congratulations, a stray reel. Those chats are
 * doctor-assigned from some earlier real question, so any later message from that parent
 * re-arms the 15-min SLA: the doctor group gets pinged and the hotline gets called for a
 * bingo entry.
 *
 * SAFETY: messages NOBODY. It only flips Chat.status server-side, and picard scopes it to
 * chats that are on THIS board right now. If the parent writes again, picard opens a
 * fresh chat — nothing is lost, the clock just stops.
 */
export async function closeSupportChat(
  chatId: number,
  reason = "not a question"
): Promise<CloseSupportChatResponse> {
  const { data } = await apiClient.post<CloseSupportChatResponse>(
    "bot/support-portal/support-chat/close/",
    { chat_id: chatId, reason }
  );
  return data;
}

/* ---------- Pulse: recently answered conversations ---------- */
// GET the SAME support-portal chat-list, but the answered side of it: non-pending
// conversations, newest-answered first (sort=desc on last_message_at). "Answered" =
// a DOCTOR sent the last message (sender_type === "doctor") — a parent who wrote back
// after the doctor is not an answer, it is a new pending turn, so we drop those.
//
// SAFETY: read-only, like every other fetch here. It powers a DISPLAY-ONLY panel; it
// has no action, no link that sends, nothing on the wire. OPTIONAL: a failure returns
// [] and the Pulse simply renders its empty state — it can never break the queue.

export type DeliveryStatus = "delivered" | "undelivered" | null;

export interface AnsweredChat {
  id: number;
  name: string;
  type: ChatType | string;
  doctorName: string | null;
  answeredAt: string | null;
  delivery: DeliveryStatus;
}

export async function fetchRecentlyAnswered(): Promise<AnsweredChat[]> {
  // picard sets conversation.status = "replied" the moment a doctor answers (see
  // save_doctor_message) — that IS the "answered" state, not open/close. A parent who
  // writes back flips it to doctor_pending, so a replied row is genuinely answered.
  const { data } = await apiClient.get<ChatListResponse>(
    "bot/support-portal/conversation/chat-list/",
    {
      params: {
        status: "replied",
        type: "qna,doctor_care,followup",
        sort: "desc",
      },
    }
  );
  return (data.messages ?? [])
    .filter((m) => m.sender_type === "doctor")
    .slice(0, 8)
    .map((m) => ({
      id: m.id,
      name: m.user_name || "Unknown customer",
      type: m.type,
      doctorName: m.doctor_name,
      answeredAt: m.updated_at,
      delivery: (m.delivery ?? null) as DeliveryStatus,
    }));
}

/* ---------- header: pending-question counts (the operational glance) ---------- */
// GET /bot/support-portal/conversation/count/pending/ — the same endpoint the retired
// Home's "ACTIONABLE QNAs" card read. Per type (qna / followup / doctor_care) picard
// returns, over ALL `doctor_pending` conversations (support_portal_views.py
// get_pending_chat_count_support):
//   pending    — every doctor_pending conversation of that type
//   actionable — the subset past its SLA window (get_actionable_chats_query — the SLA
//                predicate ONLY; a snoozed breach still counts, it is still breached)
//   snoozed    — the subset with a live conversation Snooze
// This is what keeps the glance meaningful at zero breaches: "4 pending, 0 past SLA"
// is situational awareness; a blank strip is not.

export interface PendingCountBucket {
  pending: number;
  actionable: number;
  snoozed: number;
}

export interface PendingCounts {
  qna: PendingCountBucket;
  followup: PendingCountBucket;
  doctor_care: PendingCountBucket;
}

function isBucket(b: unknown): b is PendingCountBucket {
  return (
    typeof b === "object" &&
    b !== null &&
    typeof (b as PendingCountBucket).pending === "number" &&
    typeof (b as PendingCountBucket).actionable === "number"
  );
}

export async function fetchPendingCounts(): Promise<PendingCounts | null> {
  const { data } = await apiClient.get<Partial<PendingCounts>>(
    "bot/support-portal/conversation/count/pending/"
  );
  // Never render numbers we did not actually get — a malformed payload reads "—".
  if (
    isBucket(data?.qna) &&
    isBucket(data?.followup) &&
    isBucket(data?.doctor_care)
  ) {
    return data as PendingCounts;
  }
  return null;
}

/* ---------- P0: actionable appointments (picard's own verdict) ---------- */
// GET /consultation/appointment/fetch/?status=custom_filter
//
// SAME SOURCE AS THE OLD HOME CARD. Home's "Actionable Appts" read
// POST /consultation/appointment/actionable/count/, which counts exactly the rows this
// list annotates with `is_actionable` (consultation/views.py — the two predicates are
// character-for-character identical), minus the ones with a live `appointment` Snooze.
// So: take the list, keep `is_actionable && !is_snoozed`, and the lane's card count is
// Home's `action_count` — with the rows attached instead of just a number.
//
// picard's definition of actionable (consultation/views.py:210 / :356):
//   created                                          -> booked, nobody has scheduled it
//   scheduled            & scheduled_at <= now +10m  -> imminent (or already overdue)
//   pending_confirmation & scheduled_at <= now +15m  -> imminent and still unconfirmed
//   on_going             & scheduled_at <= now -45m  -> running long / stuck
//   consultation_done    & scheduled_at <= now -1d   -> never closed out
// `custom_filter` returns that set (actionable first), so page 1 always carries it.

export interface AppointmentRow {
  id: number;
  name: string | null;
  patient_name: string | null;
  phone_number: string | null;
  scheduled_at: string | null;
  preferred_time: string | null;
  appointment_status: string;
  appointment_type: string | null;
  doctor_name: string | null;
  is_actionable: boolean;
  is_snoozed: boolean;
  /** picard sends the id of the appointment's Snooze row, or "" when it has none. */
  snooze_id: number | "";
}

interface AppointmentListResponse {
  count: number;
  results: AppointmentRow[];
}

/**
 * Returns picard's raw `custom_filter` rows — actionable AND snoozed-actionable —
 * so the caller can both build the live P0 lane (`is_actionable && !is_snoozed`)
 * and keep the snoozed rows discoverable instead of vanished.
 *
 * API LIMITATION (do not paper over it in the UI): `custom_filter` is picard's
 * actionable set plus never-closed-out consults. It does NOT return today's full
 * schedule — a confirmed appointment three hours out is absent until it is 10 min
 * from starting. So the glance can honestly say "N need action · M snoozed", but
 * NOT "K appointments today"; that would need a second fetch (?scheduled_date=)
 * we deliberately don't make.
 */
export async function fetchAppointments(): Promise<AppointmentRow[]> {
  const { data } = await apiClient.get<AppointmentListResponse>(
    "consultation/appointment/fetch/",
    { params: { status: "custom_filter", page_size: 100 } }
  );
  return data.results ?? [];
}

/* ---------- header: doctor-app reply health ---------- */
// GET /whatsapp/chats-count-v2 — the one signal worth keeping off the retired Home
// screen. `doctor_reply.raise_alert` is picard's "no doctor has replied on the app in
// the last 5 minutes" watchdog: if it fires, every P0 in the queue is about to get
// worse. One field off one GET.
//
// NOTE: this endpoint 500s against the local stack (see e2e/smoke.spec.js). The
// indicator is therefore strictly optional — on any failure it renders nothing rather
// than claiming health it cannot verify.

export interface DoctorReplyHealth {
  raise_alert: boolean;
  last_msg_time?: string | null;
}

export async function fetchDoctorReplyHealth(): Promise<DoctorReplyHealth | null> {
  const { data } = await apiClient.get<{ doctor_reply?: DoctorReplyHealth }>(
    "whatsapp/chats-count-v2"
  );
  const health = data?.doctor_reply;
  return health && typeof health.raise_alert === "boolean" ? health : null;
}

/* ---------- Clinic health strip (bots + delivery) ---------- */
// The SAME whatsapp/chats-count-v2 GET, but the three signals the retired Home showed
// and this app had been throwing away:
//   read_bot / write_bot .raise_alert  — is the WhatsApp sync bot connected? (down => a
//        whole direction of messaging is silently broken)
//   doctor_reply.sync_alert            — a doctor answered but the outbound message is
//        stuck UNSYNCED (never reached the parent) — the delivery gap the care team is
//        blind to today.
// OPTIONAL, like the reply pill: on any failure (this endpoint 500s on some stacks) the
// whole strip renders nothing rather than claim health it can't verify.

export interface BotHealth {
  raise_alert: boolean;
  heartbeat_time?: string | null;
}

export interface ClinicHealth {
  readBot: BotHealth;
  writeBot: BotHealth;
  doctorReplyAlert: boolean;
  /** a doctor answer is stuck undelivered right now */
  undeliveredAlert: boolean;
}

interface ChatsCountV2 {
  read_bot?: { raise_alert?: unknown; heartbeat_time?: string | null };
  write_bot?: { raise_alert?: unknown; heartbeat_time?: string | null };
  doctor_reply?: { raise_alert?: unknown; sync_alert?: unknown };
}

export async function fetchClinicHealth(): Promise<ClinicHealth | null> {
  const { data } = await apiClient.get<ChatsCountV2>("whatsapp/chats-count-v2");
  const rb = data?.read_bot;
  const wb = data?.write_bot;
  const dr = data?.doctor_reply;
  // Require the bot blocks to be well-formed; the endpoint is the source of truth and a
  // malformed payload must render nothing, never a false "all connected".
  if (
    !rb ||
    !wb ||
    typeof rb.raise_alert !== "boolean" ||
    typeof wb.raise_alert !== "boolean"
  ) {
    return null;
  }
  return {
    readBot: {
      raise_alert: rb.raise_alert,
      heartbeat_time: rb.heartbeat_time ?? null,
    },
    writeBot: {
      raise_alert: wb.raise_alert,
      heartbeat_time: wb.heartbeat_time ?? null,
    },
    doctorReplyAlert: dr?.raise_alert === true,
    undeliveredAlert: dr?.sync_alert === true,
  };
}

/* ---------- Clinic health: every waplify session's connection state ---------- */
// GET /whatsapp/sessions/status/ — picard's AdminSession list (kept live by the
// reconcile_wa_session_status cron; waplify is source of truth). Enumerates ALL the
// numbers/bots — read-bot instances, write bots, doctor bot, Care Manager, etc — so a
// CM can see at a glance that every one is connected, and exactly which is down.
// Read-only; renders nothing on failure (never claims "all connected" it can't verify).

export interface WaSession {
  session_name: string;
  phone_number: string;
  description: string | null;
  type: string[];
  status: string; // connected | disconnected | error | created
  connection_status: string | null;
  last_used_at: string | null;
}

export interface WaSessions {
  sessions: WaSession[];
  total: number;
  connected: number;
  down: number;
}

export function isSessionDown(s: WaSession): boolean {
  return s.status !== "connected";
}

export async function fetchWaSessions(): Promise<WaSessions | null> {
  const { data } = await apiClient.get<WaSessions>("whatsapp/sessions/status/");
  if (!data || !Array.isArray(data.sessions)) return null;
  return data;
}

/* ---------- P2: retention tasks (picard expiry scan) ---------- */
// GET /tasks/get-support-tasks/ — we keep only type === "subscription_expired".

export interface TaskSubscriptionDetail {
  subscription_name?: string | null;
  started_at?: string | null;
  ending_at?: string | null;
}

export interface TaskUserDetails {
  user_name?: string | null;
  user_mobile?: string | null;
}

/**
 * Retention CRM pipeline state — picard stores it under Task.remarks.crm and the
 * get-support-tasks serializer surfaces it as a top-level `crm` (null until a CM first
 * advances the task). SAFETY: this is a pure DB annotation; recording a stage NEVER
 * messages the customer — the human sends from waplify. Terminal stages
 * (renewed/not_interested/no_response) close the task server-side, so it drops out of
 * the pending queue.
 */
export interface TaskCrm {
  stage?: string | null;
  contacted_at?: string | null;
  note?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface SupportTask {
  id: number;
  type: string;
  task: string;
  status: string;
  created_at: string | null;
  scheduled_at: string | null;
  all_details: {
    user_details?: TaskUserDetails | null;
    details?: TaskSubscriptionDetail[] | null;
  } | null;
  remarks: { system?: { days_remaining?: number } | null } | null;
  crm?: TaskCrm | null;
}

interface SupportTasksResponse {
  data: SupportTask[];
  page: number;
}

export async function fetchExpiryTasks(): Promise<SupportTask[]> {
  // Filter SERVER-side. The unfiltered endpoint returns a paginated page-1 that in
  // prod is 100 rows of `wa_tagged`, so a client-side filter finds zero expiry tasks.
  // status=pending keeps already-actioned tasks out of the queue.
  const { data } = await apiClient.get<SupportTasksResponse>(
    "tasks/get-support-tasks/",
    { params: { type: "subscription_expired", status: "pending" } }
  );
  return (data.data ?? []).filter((t) => t.type === "subscription_expired");
}

/* ---------- P0: "Parents waiting" care flags (picard care-flag tasks) ---------- */
// GET /tasks/get-support-tasks/?type=unanswered_message&status=pending — SAME call
// shape as fetchExpiryTasks (auth/client reused). Each task carries a `care` block: a
// parent messaged a Babynama WhatsApp number and is waiting for a human reply NOW.
//
// SAFETY: strictly read-only, like the expiry fetch. The card's only outward action is
// a LINK into waplify where a human replies; the suggested reply is COPYABLE text, never
// a send. Nothing here — or anywhere in this file — can put a message on the wire.

export type CareKind = "medical" | "non-medical";

export interface CareFlag {
  kind: CareKind;
  /** short triage reason, e.g. "asks how to book" */
  reason: string;
  wait_minutes: number;
  /** the Babynama number the parent messaged */
  source_number: string;
  /** human label for that number, e.g. "Care Manager" / "Community Support" */
  source_label: string;
  /** the parent's actual message */
  message_excerpt: string;
  /** suggested reply to COPY — NULL for medical (no auto-draft for clinical questions) */
  draft_reply: string | null;
  /** digits for the waplify ?phone= deep-link */
  deeplink_phone: string;
}

export interface CareTask {
  id: number;
  type: string;
  task: string;
  status: string;
  scheduled_at: string | null;
  all_details: {
    user_details?: { user_mobile?: string | null } | null;
  } | null;
  care: CareFlag;
}

interface CareTasksResponse {
  data: CareTask[];
  page: number;
}

export async function fetchCareTasks(): Promise<CareTask[]> {
  // Filter SERVER-side (type + status), mirroring fetchExpiryTasks — the unfiltered
  // endpoint is a paginated page-1 of mixed task types.
  const { data } = await apiClient.get<CareTasksResponse>(
    "tasks/get-support-tasks/",
    { params: { type: "unanswered_message", status: "pending" } }
  );
  // Defensive: keep only rows that actually carry a care block (a malformed row must
  // not crash the lane — it just doesn't render).
  return (data.data ?? []).filter(
    (t) => t.type === "unanswered_message" && t.care != null
  );
}

/* ---------- actions ---------- */
// SAFETY: every call below is an internal DB state change. NOTHING here sends a
// WhatsApp message, an SMS, a push, or places a call.
//
//   POST tasks/edit-task-status/     {task_id, status:"done"}            -> Task.status
//   POST tasks/edit/                 {task_id, scheduled_at}             -> Task.scheduled_at
//   POST bot/support-portal/conversation/notify-doctor/
//                                    {conversation_id, snooze_duration,
//                                     is_notification: false}            -> Snooze row
//
// picard's notify_doctor (bot/support_portal_views.py:370) fires an FCM push to the
// assigned doctor ONLY inside `if is_notification:` — we always send `false`, so the
// view falls straight through to the Snooze branch. It is the only endpoint we touch
// that has any notification path at all.

/* ---------- P2 · hand-off to a human on WhatsApp ---------- */
// SAFETY: this is a LINK, not a send. It opens waplify's chat UI in a new tab with
// the customer pre-selected; a human then types and sends the message themselves.
// There is no request, no template, no queue — nothing in this file can put a
// message on the wire. (The old "Send renewal" auto-outreach stub stays deleted.)

const WAPLIFY_URL = (
  import.meta.env.VITE_WAPLIFY_URL ?? "https://app.waplify.in"
).replace(/\/+$/, "");

/**
 * `?phone=` deep-linking is a waplify-side change that is NOT deployed yet. Until it
 * ships waplify ignores the param and lands the CM on the chat list — which is why
 * the card ALSO copies the number to the clipboard, so they can paste it into
 * waplify's search box. When waplify ships the param, the chat auto-opens and the
 * clipboard copy becomes a harmless no-op. Degrades gracefully, no feature flag.
 */
export function waplifyChatHref(mobile: string): string | null {
  const digits = mobile.replace(/\D/g, "");
  return digits ? `${WAPLIFY_URL}/whatsapp-chat?phone=${digits}` : null;
}

/**
 * Best-effort clipboard write. The Clipboard API is unavailable on non-secure
 * origins and can be permission-denied — never let that throw, and never let it
 * stop the link from opening.
 */
export async function copyNumber(mobile: string): Promise<boolean> {
  const digits = mobile.replace(/\D/g, "");
  if (!digits) return false;
  try {
    await navigator.clipboard?.writeText(digits);
    return true;
  } catch {
    return false;
  }
}

/**
 * Best-effort clipboard write for arbitrary text — the "Parents waiting" suggested
 * reply. Same guarantees as copyNumber: never throws, returns whether it landed. It
 * COPIES; it does not send. A human pastes it into waplify and sends it there.
 */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    await navigator.clipboard?.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* ---------- internal deep links (shared by the row and the 360 panel) ---------- */

/** Which picard row a card is backed by — decides the actions it can offer. */
export type ItemKind = "chat" | "task" | "appointment";

/** The User Data screen auto-searches on mount from ?mobile_number= (digits, no '+'). */
export function customerHref(mobile: string): string | null {
  const digits = mobile.replace(/\D/g, "");
  return digits ? `/userdata?mobile_number=${digits}` : null;
}

/**
 * P0 only — deep-link into the QnAs queue, which scopes its chat list to ?mobile=
 * and auto-opens the ?conversation_id= thread. Read-only: it just shows the thread.
 */
export function conversationHref(
  kind: ItemKind,
  sourceId: number,
  mobile: string
): string | null {
  if (kind !== "chat") return null;
  const params = new URLSearchParams({ conversation_id: String(sourceId) });
  const digits = mobile.replace(/\D/g, "");
  if (digits) params.set("mobile", digits);
  return `/qnas?${params.toString()}`;
}

/** picard's tasks.models.Snooze.DURATION_CHOICES. */
export type SnoozeDuration = "30_min" | "60_min" | "8am_next_day";

export const SNOOZE_OPTIONS: ReadonlyArray<{
  value: SnoozeDuration;
  label: string;
}> = [
  { value: "30_min", label: "30 minutes" },
  { value: "60_min", label: "60 minutes" },
  { value: "8am_next_day", label: "8 AM next day" },
];

const IST_OFFSET_MS = 330 * 60_000;

/**
 * Mirror of picard's `calculate_snooze_end_time` (tasks/views.py), which resolves
 * durations in Asia/Kolkata. We need it client-side because a Task has no Snooze
 * row available — `CreateSnoozeView` only accepts `chat` / `appointment` source
 * tables — so a task is snoozed by pushing its `scheduled_at` forward instead.
 */
export function snoozeUntil(duration: SnoozeDuration, now: number): Date {
  if (duration === "30_min") return new Date(now + 30 * 60_000);
  if (duration === "60_min") return new Date(now + 60 * 60_000);
  const ist = new Date(now + IST_OFFSET_MS);
  let target =
    Date.UTC(
      ist.getUTCFullYear(),
      ist.getUTCMonth(),
      ist.getUTCDate(),
      8,
      0,
      0
    ) - IST_OFFSET_MS;
  if (target <= now) target += 86_400_000;
  return new Date(target);
}

export interface TaskStatusResponse {
  message: string;
  task_status: string;
  updated_at: string;
}

/** P2 · Mark done — flips Task.status to `done`; it drops out of the pending queue. */
export async function markTaskDone(
  taskId: number
): Promise<TaskStatusResponse> {
  const { data } = await apiClient.post<TaskStatusResponse>(
    "tasks/edit-task-status/",
    { task_id: taskId, status: "done" }
  );
  return data;
}

/* ---------- Retention · advance a task through the renewal pipeline ---------- */
// POST tasks/crm-stage/ {task_id, stage, note?} -> records the CRM stage on the task.
//
// SAFETY: this is a pure internal state change (Task.remarks.crm), IDENTICAL in spirit
// to markTaskDone — it messages NOBODY. The human contacts the customer in waplify; this
// only records where they sit in the renewal pipeline. Terminal stages
// (renewed/not_interested/no_response) also flip Task.status -> done server-side so the
// card drops out of the pending queue; call_back keeps the task open in its stage.

/** picard's tasks/views.py CRM_STAGES — the only values crm-stage accepts. */
export type CrmStage =
  | "at_risk"
  | "contacted"
  | "replied"
  | "renewed"
  | "not_interested"
  | "no_response"
  | "call_back";

export interface CrmStageResponse {
  message: string;
  stage: CrmStage;
  /** picard's Task.status after the write — "done" for a terminal stage, else "pending". */
  status: string;
}

export async function setCrmStage(
  taskId: number,
  stage: CrmStage,
  note?: string
): Promise<CrmStageResponse> {
  const { data } = await apiClient.post<CrmStageResponse>("tasks/crm-stage/", {
    task_id: taskId,
    stage,
    note,
  });
  return data;
}

export interface EditTaskResponse {
  message: string;
  task_status: string | null;
  scheduled_at: string | null;
  updated_at: string;
}

/** P2 · Snooze — reschedule the Task into the future; the queue hides future work. */
export async function snoozeTask(
  taskId: number,
  duration: SnoozeDuration,
  now: number = Date.now()
): Promise<EditTaskResponse> {
  const { data } = await apiClient.post<EditTaskResponse>("tasks/edit/", {
    task_id: taskId,
    scheduled_at: snoozeUntil(duration, now).toISOString(),
  });
  return data;
}

export interface SnoozeConversationResponse {
  message: string;
  snooze_duration?: SnoozeDuration;
  snooze_end_time?: string;
}

/**
 * P0 · Snooze — creates a `conversation` Snooze row. picard then reports the chat as
 * `is_snoozed: true` / `is_actionable: false`, so it leaves the queue.
 * `is_notification: false` is MANDATORY: with it omitted picard defaults to `true`
 * and pushes an FCM notification to the assigned doctor.
 */
export async function snoozeConversation(
  conversationId: number,
  duration: SnoozeDuration
): Promise<SnoozeConversationResponse> {
  const { data } = await apiClient.post<SnoozeConversationResponse>(
    "bot/support-portal/conversation/notify-doctor/",
    {
      conversation_id: conversationId,
      snooze_duration: duration,
      is_notification: false,
    }
  );
  return data;
}

export interface SnoozeAppointmentResponse {
  snooze_id: number;
  snooze_duration: SnoozeDuration;
  snooze_end_time: string;
  source_table: string;
  source_id: number;
}

/**
 * P0 · Snooze an appointment — writes an `appointment` Snooze row (tasks/views.py).
 * picard's actionable-count query excludes snoozed appointments and the list serializer
 * reports `is_snoozed: true`, so the card leaves the queue.
 *
 * CREATE-OR-EDIT, deliberately: `Snooze` is unique on (source_table, source_id) and
 * CreateSnoozeView 400s ("a snooze already exists") on the second call — and picard
 * NEVER deletes the row, it only moves `snooze_end_time` into the past. So an
 * appointment that was ever snoozed can only be snoozed again through EditSnoozeView.
 * The Appointments screen already does exactly this (AppointmentDesktopView); a
 * create-only path here would have failed the moment a CM snoozed the same appointment
 * twice.
 *
 * SAFETY: both views write ONE Snooze row and return. Neither has a notification branch
 * at all — unlike the conversation snooze there is not even an `is_notification` flag to
 * get wrong. Nothing here can message the customer or ping the doctor.
 */
export async function snoozeAppointment(
  appointmentId: number,
  duration: SnoozeDuration,
  snoozeId: number | null
): Promise<SnoozeAppointmentResponse> {
  if (snoozeId) {
    const { data } = await apiClient.post<{ data: SnoozeAppointmentResponse }>(
      "tasks/alert/edit-snooze/",
      { snooze_id: snoozeId, snooze_duration: duration }
    );
    return data.data;
  }
  const { data } = await apiClient.post<SnoozeAppointmentResponse>(
    "tasks/alert/create-snooze/",
    {
      alert_source_table: "appointment",
      alert_source_id: appointmentId,
      snooze_duration: duration,
    }
  );
  return data;
}

interface PicardError {
  error?: string;
  message?: string;
}

/** picard errors come back as {error}, {message}, or a bare string. */
export function errorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const body = err.response?.data as PicardError | string | undefined;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") {
      const text = body.error ?? body.message;
      if (text) return text;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}
